import SpeedTest from '@cloudflare/speedtest'
import {CONNECTION_TEST_PING_URL} from './api'

export interface ProviderMeasurement {
  latencyMs: number
  jitterMs: number
  source: 'cloudflare' | 'http-ping'
}

const CF_OVERALL_TIMEOUT_MS = 20000
const CF_LATENCY_PACKETS = 20

export async function measureWithCloudflare(
  signal?: AbortSignal
): Promise<ProviderMeasurement> {
  if (typeof window === 'undefined') {
    throw new Error('Cloudflare Speedtest requires a browser environment')
  }

  return new Promise<ProviderMeasurement>((resolve, reject) => {
    let finished = false

    const engine = new SpeedTest({
      autoStart: false,
      measurements: [{type: 'latency', numPackets: CF_LATENCY_PACKETS}],
    })

    const cleanup = () => {
      try {
        engine.pause()
      } catch {
        // ignore
      }
    }

    const timer = setTimeout(() => {
      if (finished) return
      finished = true
      cleanup()
      reject(new Error('Cloudflare speedtest timed out'))
    }, CF_OVERALL_TIMEOUT_MS)

    const abortHandler = () => {
      if (finished) return
      finished = true
      clearTimeout(timer)
      cleanup()
      reject(new Error('cancelled'))
    }

    if (signal) {
      if (signal.aborted) return abortHandler()
      signal.addEventListener('abort', abortHandler, {once: true})
    }

    engine.onFinish = (results) => {
      if (finished) return
      finished = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', abortHandler)

      const latency = results.getUnloadedLatency()
      const jitter = results.getUnloadedJitter()

      if (latency === undefined || jitter === undefined) {
        reject(new Error('Cloudflare speedtest returned no latency data'))
        return
      }

      resolve({
        latencyMs: Math.max(0, Math.round(latency)),
        jitterMs: Math.max(0, Math.round(jitter)),
        source: 'cloudflare',
      })
    }

    engine.onError = (err) => {
      if (finished) return
      finished = true
      clearTimeout(timer)
      cleanup()
      signal?.removeEventListener('abort', abortHandler)
      reject(new Error(typeof err === 'string' ? err : 'Cloudflare speedtest failed'))
    }

    try {
      engine.play()
    } catch (err: any) {
      if (finished) return
      finished = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', abortHandler)
      reject(err instanceof Error ? err : new Error('Cloudflare speedtest failed to start'))
    }
  })
}

interface HttpPingOptions {
  totalSamples?: number
  warmupSamples?: number
  intervalMs?: number
  timeoutMs?: number
}

const HTTP_DEFAULTS: Required<HttpPingOptions> = {
  totalSamples: 10,
  warmupSamples: 3,
  intervalMs: 120,
  timeoutMs: 3000,
}

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new Error('cancelled'))
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(t)
      reject(new Error('cancelled'))
    }
    signal?.addEventListener('abort', onAbort, {once: true})
  })

async function singleHeadPing(url: string, timeoutMs: number, signal?: AbortSignal) {
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  if (signal) {
    if (signal.aborted) throw new Error('cancelled')
    signal.addEventListener('abort', onAbort, {once: true})
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  const start = performance.now()
  try {
    const res = await fetch(`${url}?t=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
      credentials: 'omit',
    })
    if (!res.ok) throw new Error(`Ping failed: ${res.status}`)
    return performance.now() - start
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }
}

const median = (values: number[]): number => {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

export async function measureWithHttpPing(
  signal?: AbortSignal,
  options?: HttpPingOptions
): Promise<ProviderMeasurement> {
  const opts = {...HTTP_DEFAULTS, ...(options || {})}
  const samples: number[] = []
  const total = opts.warmupSamples + opts.totalSamples

  for (let i = 0; i < total; i++) {
    if (signal?.aborted) throw new Error('cancelled')
    try {
      const rtt = await singleHeadPing(CONNECTION_TEST_PING_URL, opts.timeoutMs, signal)
      if (i >= opts.warmupSamples) samples.push(rtt)
    } catch (err) {
      if ((err as Error)?.message === 'cancelled') throw err
      if (i >= opts.warmupSamples) samples.push(opts.timeoutMs)
    }
    if (i < total - 1) await wait(opts.intervalMs, signal)
  }

  if (samples.length === 0) throw new Error('No samples collected')

  const latency = median(samples)

  let jitter = 0
  if (samples.length > 1) {
    let sumDelta = 0
    for (let i = 1; i < samples.length; i++) {
      sumDelta += Math.abs(samples[i] - samples[i - 1])
    }
    jitter = sumDelta / (samples.length - 1)
  }

  return {
    latencyMs: Math.max(0, Math.round(latency)),
    jitterMs: Math.max(0, Math.round(jitter)),
    source: 'http-ping',
  }
}

export async function runPreferredMeasurement(
  signal?: AbortSignal
): Promise<ProviderMeasurement> {
  try {
    return await measureWithCloudflare(signal)
  } catch (err) {
    if ((err as Error)?.message === 'cancelled') throw err
    return await measureWithHttpPing(signal)
  }
}
